/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { initializeApp, getApps, getApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithPopup, 
  GoogleAuthProvider, 
  onAuthStateChanged, 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInAnonymously,
  signOut,
  sendPasswordResetEmail,
  User as FirebaseUser 
} from 'firebase/auth';
import firebaseConfig from '../../firebase-applet-config.json';

// Initialize Firebase app singleton
const app = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);
export const auth = getAuth(app);

const provider = new GoogleAuthProvider();
// Add scopes required by user for Gmail integration
provider.addScope('https://www.googleapis.com/auth/gmail.send');
provider.addScope('https://www.googleapis.com/auth/gmail.readonly');
provider.addScope('https://www.googleapis.com/auth/gmail.modify');

// Prompt specifically for user choice to ensure seamless login
provider.setCustomParameters({
  prompt: 'select_account'
});

// Flag to indicate if we are in the middle of a sign-in flow.
let isSigningIn = false;
// Cache the access token in memory.
let cachedAccessToken: string | null = null;
let currentAuthUser: FirebaseUser | null = null;
let authStatus: 'AUTH_LOADING' | 'AUTHENTICATED' | 'UNAUTHENTICATED' = 'AUTH_LOADING';
let isAuthInitialized = false;

export type AuthState = 'AUTH_LOADING' | 'AUTHENTICATED' | 'UNAUTHENTICATED';

// Listeners array to update React state dynamically
const authListeners: Array<(user: FirebaseUser | null, token: string | null, status: AuthState) => void> = [];

export const registerAuthListener = (callback: (user: FirebaseUser | null, token: string | null, status: AuthState) => void) => {
  authListeners.push(callback);
  // Initial call with current state
  callback(currentAuthUser, cachedAccessToken, authStatus);
  return () => {
    const idx = authListeners.indexOf(callback);
    if (idx !== -1) authListeners.splice(idx, 1);
  };
};

const notifyListeners = () => {
  for (const listener of authListeners) {
    listener(currentAuthUser, cachedAccessToken, authStatus);
  }
};

// Initialize auth state listener
onAuthStateChanged(auth, async (user: FirebaseUser | null) => {
  isAuthInitialized = true;
  currentAuthUser = user;
  if (user) {
    authStatus = 'AUTHENTICATED';
  } else {
    authStatus = 'UNAUTHENTICATED';
    cachedAccessToken = null;
  }
  notifyListeners();
});

export const getAuthStatus = (): AuthState => {
  return authStatus;
};

export const isAuthReady = (): boolean => {
  return isAuthInitialized;
};

/**
 * Sign in with Email and Password using Firebase Authentication
 */
export const loginWithFirebaseEmail = async (email: string, password: string): Promise<FirebaseUser> => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email.trim(), password);
    currentAuthUser = userCredential.user;
    notifyListeners();
    return userCredential.user;
  } catch (error: any) {
    // Return or re-throw without polluting console with high-priority errors during initial trial
    throw error;
  }
};

/**
 * Create a new user with Email and Password in Firebase Authentication
 */
export const registerWithFirebaseEmail = async (email: string, password: string): Promise<FirebaseUser> => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email.trim(), password);
    currentAuthUser = userCredential.user;
    notifyListeners();
    return userCredential.user;
  } catch (error: any) {
    throw error;
  }
};

/**
 * Sign in anonymously with Firebase Authentication for authenticated session access
 */
export const loginAnonymously = async (): Promise<FirebaseUser> => {
  try {
    const userCredential = await signInAnonymously(auth);
    currentAuthUser = userCredential.user;
    notifyListeners();
    return userCredential.user;
  } catch (error: any) {
    console.error('Firebase anonymous auth error:', error);
    throw error;
  }
};

/**
 * Send official Firebase password reset email
 */
export const resetFirebasePassword = async (email: string): Promise<void> => {
  try {
    await sendPasswordResetEmail(auth, email.trim());
  } catch (error: any) {
    console.error('Firebase password reset error:', error);
    throw error;
  }
};

// Sign-in with Microsoft/Google Popup
export const googleSignIn = async (): Promise<{ user: FirebaseUser; accessToken: string } | null> => {
  try {
    isSigningIn = true;
    const result = await signInWithPopup(auth, provider);
    const credential = GoogleAuthProvider.credentialFromResult(result);
    if (!credential?.accessToken) {
      throw new Error('Failed to retrieve Google OAuth access token from Firebase Auth');
    }

    cachedAccessToken = credential.accessToken;
    currentAuthUser = result.user;
    notifyListeners();
    return { user: result.user, accessToken: cachedAccessToken };
  } catch (error: any) {
    console.error('Core Google sign in error:', error);
    throw error;
  } finally {
    isSigningIn = false;
  }
};

export const getAccessToken = (): string | null => {
  return cachedAccessToken;
};

export const getFirebaseUser = (): FirebaseUser | null => {
  return currentAuthUser;
};

export const getGoogleUser = (): FirebaseUser | null => {
  return currentAuthUser;
};

export const logoutFirebaseAuth = async () => {
  await signOut(auth);
  cachedAccessToken = null;
  currentAuthUser = null;
  notifyListeners();
};

export const logoutGoogle = logoutFirebaseAuth;

